import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import {
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import { UserRole } from '@mm/state/model/CompanyUsersModel';
import {
  isBilling2023Subscription,
  SubscriptionModel,
} from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
} from '@mm/state/selectors/facebookPageSelector';
import {
  defaultProPlanSelector,
  defaultProUnicornPlanSelector,
  getCurrentSubscription,
} from '@mm/state/selectors/subscriptionSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { activeCompanyUsers } from '@mm/state/selectors/viewSelectors';
import moment from 'moment';
import { useLandmineOpensCRUDActions } from '@mm/state/actions/LandmineOpensCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { INewBillingModal2023Props } from '@mm/state/modules/modalsState/props';

export const useFreeTrialUpgradeModal = (
  facebookPageOverride?: EnhancedBillingPage,
  upgradePlan?: 'pro' | 'unicorn',
) => {
  const modalActions = useModalActions();
  const subscriptionCRUDActions = useSubscriptionCRUDActions();

  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const activeMessagesStatistic = useSelector(activeMessagesStatisticSelector);
  const facebookPage = facebookPageOverride || activeFacebookPage;
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);
  const defaultProPlan = useSelector(defaultProPlanSelector);
  const defaultUnicornPlan = useSelector(defaultProUnicornPlanSelector);
  const planToUse =
    upgradePlan === 'unicorn' ? defaultUnicornPlan : defaultProPlan;
  const [landmineOpenId, setLandmineOpenId] = useState<number | undefined>();
  const landmineOpensCRUDActions = useLandmineOpensCRUDActions();
  const appNavigationHistory = useSelector(
    (state: StoreState) => state.view.appNavigationHistory,
  );
  const sendConversionInfo = async (name: string) => {
    if (landmineOpenId) {
      return landmineOpenId;
    }
    const res = await landmineOpensCRUDActions.create({
      landmineName: name,
      navigationHistory: appNavigationHistory,
      planName: 'pro',
      billingOpened: true,
    });
    if (res && res.id) {
      setLandmineOpenId(res.id);
      return res.id;
    }
  };
  const onAlertClick = async (name: string) => {
    const id = await sendConversionInfo(name);
    if (planToUse && id) {
      modalActions.openBillingModal({
        facebookPage,
        plan: planToUse,
        landmineOpenId,
        stayOnPage: false,
      });
    }
  };

  const isFreePlan = facebookPage?.pageBillingType === PageBillingType.Free;

  const isFreeTrial = !!facebookPage?.redemption?.freeTrial;
  const isExpired =
    facebookPage?.redemption &&
    moment().diff(moment(facebookPage.redemption.expiresAt)) > 0;

  const isSendLimitReached = !!activeMessagesStatistic.limitReachedAt;

  const isFreeTrialPromotionActive = isFreeTrial && !isExpired;

  const freeTrialPromotion = isFreeTrialPromotionActive
    ? facebookPage?.redemption
    : undefined;

  const pageCreatedAt = moment(facebookPage?.createdAt);
  const diffInDays = moment().diff(pageCreatedAt, 'days');
  const isFreeTrialWithManuallyAssignedPromocode =
    isFreeTrialPromotionActive && diffInDays > 30;

  const subscriptionFromSubscriptions = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );

  const currentSubscription: SubscriptionModel | undefined =
    facebookPage?.subscription || subscriptionFromSubscriptions;

  const companyUsers = useSelector(activeCompanyUsers);
  const session = useSelector(selectSessionSelector);

  const isFreeTrialPromotionWithoutSubscription =
    isFreeTrialPromotionActive && !currentSubscription;

  const isFreeTrialPromotionWithFutureSubscription =
    isFreeTrialPromotionActive && currentSubscription?.state === 'future';

  const allowedToManageBilling = useMemo(() => {
    const currentUser = companyUsers.find(user => user.id === session.id);

    return (
      currentUser &&
      (currentUser.role === UserRole.Admin ||
        currentUser.role === UserRole.Owner)
    );
  }, [companyUsers, session]);

  useEffect(() => {
    if (
      facebookPage?.subscription?.id &&
      (!currentSubscription ||
        currentSubscription.id !== facebookPage?.subscription?.id) &&
      allowedToManageBilling
    ) {
      subscriptionCRUDActions.getCurrentSubscription(
        facebookPage?.id,
        false,
        facebookPage?.subscription?.id,
        isNewBillingSubscription,
      );
    }
  }, [facebookPage?.subscription?.id, allowedToManageBilling]);

  const openFreeTrialUpgradeModal = (
    modalProps: INewBillingModal2023Props,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.preventDefault();
    e?.stopPropagation();
    openFreeTrialUpgradeModalPromotional({ ...modalProps });
  };

  const openFreeTrialUpgradeModalPromotional = (
    modalProps: INewBillingModal2023Props,
  ) => {
    modalActions.openNewBillingModal2023({ ...modalProps });
  };

  return {
    openFreeTrialUpgradeModal,
    openFreeTrialUpgradeModalPromotional,
    currentSubscription,
    freeTrialPromotion,
    isExpired,
    isSendLimitReached,
    isFreePlan,
    isFreeTrial,
    isFreeTrialPromotionActive,
    isFreeTrialPromotionWithoutSubscription,
    isFreeTrialPromotionWithFutureSubscription,
    allowedToManageBilling,
    onAlertClick,
    isFreeTrialWithManuallyAssignedPromocode,
  };
};
